import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Modal } from "../components/Modal";
import * as DataMaintenanceStore from "./DataMaintenanceStore";

const CountPill = ({ label, value }: { label: string; value: number }) => (
    <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700">
        <span className="text-gray-500">{label}</span>
        <span className="ml-1 font-semibold text-gray-900">{value}</span>
    </span>
);

const StatusBadge = ({ user }: { user: DataMaintenanceStore.OrphanedUserSummary }) => {
    if (user.canDelete) {
        return (
            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-200">
                <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1.5 h-3 w-3" />
                Eligible
            </span>
        );
    }

    return (
        <div className="flex flex-wrap gap-1.5">
            {user.protectionReasons.map((reason) => (
                <span key={reason} className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                    <FontAwesomeIcon icon={["fad", "shield-alt"]} className="mr-1.5 h-3 w-3" />
                    {reason}
                </span>
            ))}
        </div>
    );
};

const formatDate = (value: string) => {
    if (!value || value.startsWith("0001-01-01")) {
        return "-";
    }

    return new Date(value).toLocaleString();
};

const displayName = (user: DataMaintenanceStore.OrphanedUserSummary) => {
    const name = user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.email || user.userName || user.id;
};

const displayValue = (value: React.ReactNode, mono = false) => {
    if (value === null || value === undefined || value === "") {
        return <span className="text-gray-400">-</span>;
    }

    return <span className={`${mono ? "font-mono" : ""} break-all`}>{value}</span>;
};

const DetailField = ({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900">{displayValue(value, mono)}</dd>
    </div>
);

const EmptyDetail = ({ message }: { message: string }) => <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">{message}</div>;

const DetailSection = ({
    title,
    count,
    action,
    children,
}: {
    title: string;
    count?: number;
    action?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <section className="border-t border-gray-200 px-4 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-900">
                {title}
                {count !== undefined && <span className="ml-2 text-xs font-medium text-gray-500">{count}</span>}
            </h3>
            {action}
        </div>
        {children}
    </section>
);

const DeleteConfirmationModal = ({
    isOpen,
    detail,
    loading,
    error,
    deleting,
    result,
    onClose,
    onConfirm,
}: {
    isOpen: boolean;
    detail: DataMaintenanceStore.OrphanedUserDetail | null;
    loading: boolean;
    error: string;
    deleting: boolean;
    result: DataMaintenanceStore.DeleteOrphanedUserResult | null;
    onClose: () => void;
    onConfirm: () => void;
}) => {
    const currentDetail = result?.detail || detail;
    const protectionReasons = result?.protectionReasons?.length ? result.protectionReasons : currentDetail?.protectionReasons || [];
    const canConfirm = !!currentDetail?.canDelete && !loading && !deleting && !result?.deleted;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete Orphaned Account"
            icon={["fad", "trash-alt"]}
            iconColor="text-red-600"
            size="lg"
            closeOnOverlayClick={!deleting}
            footer={
                result?.deleted ? (
                    <Button onClick={onClose}>
                        <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-2 h-4 w-4" />
                        Close
                    </Button>
                ) : (
                    <>
                        <Button variant="outline-secondary" onClick={onClose} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={onConfirm} disabled={!canConfirm}>
                            <FontAwesomeIcon icon={["fad", deleting ? "spinner-third" : "trash-alt"]} spin={deleting} className="mr-2 h-4 w-4" />
                            Delete local account
                        </Button>
                    </>
                )
            }
        >
            <div className="space-y-4 p-6">
                {loading && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                        <FontAwesomeIcon icon={["fad", "spinner-third"]} spin className="mr-2" />
                        Loading current account diagnostics
                    </div>
                )}

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                        {error}
                    </div>
                )}

                {result?.deleted && (
                    <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-2" />
                        {result.message || "Account deleted."}
                    </div>
                )}

                {result && !result.deleted && !result.notFound && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <FontAwesomeIcon icon={["fad", "shield-alt"]} className="mr-2" />
                        {result.message || "Account is protected and was not deleted."}
                    </div>
                )}

                {currentDetail && (
                    <>
                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="break-words text-base font-semibold text-gray-900">{displayName(currentDetail)}</div>
                                    <div className="mt-1 break-all text-sm text-gray-600">{currentDetail.email || currentDetail.userName || currentDetail.id}</div>
                                    <div className="mt-1 break-all font-mono text-xs text-gray-500">{currentDetail.id}</div>
                                </div>
                                <StatusBadge user={currentDetail} />
                            </div>
                        </div>

                        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                            This deletes only local ScadsysIdentity data for this account, including local provider login rows. It does not call Microsoft, Entra, Google, Apple, Facebook, or any other external provider.
                        </div>

                        {protectionReasons.length > 0 && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                                <div className="text-sm font-medium text-amber-900">Deletion blocked</div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {protectionReasons.map((reason) => (
                                        <span key={reason} className="rounded-md bg-white px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                                            {reason}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                            <div className="mb-3 text-sm font-semibold text-gray-900">Dependency counts</div>
                            <div className="flex flex-wrap gap-1.5">
                                <CountPill label="Enabled orgs" value={currentDetail.enabledMembershipCount} />
                                <CountPill label="Disabled orgs" value={currentDetail.disabledMembershipCount} />
                                <CountPill label="Providers" value={currentDetail.providerLoginCount} />
                                <CountPill label="Claims" value={currentDetail.claimCount} />
                                <CountPill label="Metadata" value={currentDetail.metadataCount} />
                                <CountPill label="Groups" value={currentDetail.groupCount} />
                                <CountPill label="Delegated" value={currentDetail.delegatedAccessCount} />
                                <CountPill label="Credentials" value={currentDetail.credentialCount} />
                                <CountPill label="Related" value={currentDetail.relatedLocalRowCount} />
                            </div>
                            <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                                <DetailField label="Created" value={formatDate(currentDetail.dateCreated)} />
                                <DetailField label="Updated" value={formatDate(currentDetail.dateUpdated)} />
                                <DetailField label="Last login" value={formatDate(currentDetail.lastLoginDate)} />
                            </dl>
                        </div>

                        {currentDetail.relatedLocalRows.length > 0 && (
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {currentDetail.relatedLocalRows.map((row) => (
                                    <div key={row.name} className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm">
                                        <span className="break-words text-gray-700">{row.name}</span>
                                        <span className="ml-3 font-semibold text-gray-900">{row.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

const MembershipList = ({ memberships }: { memberships: DataMaintenanceStore.OrphanedUserMembershipDetail[] }) => {
    if (memberships.length === 0) {
        return <EmptyDetail message="No memberships" />;
    }

    return (
        <div className="space-y-2">
            {memberships.map((membership) => (
                <div key={`${membership.organizationId}-${membership.type}-${membership.enabled}`} className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{membership.organizationName || membership.organizationId}</span>
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{membership.type}</span>
                        <span className={`rounded-md px-2 py-0.5 text-xs ${membership.enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                            {membership.enabled ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                        <DetailField label="Organization ID" value={membership.organizationId} mono />
                        <DetailField label="Source" value={membership.source} />
                        <DetailField label="Source ID" value={membership.sourceId} mono />
                    </dl>
                </div>
            ))}
        </div>
    );
};

const DetailPanel = ({
    selectedUserId,
    detail,
    loading,
    error,
    onRevealMetadata,
    onHideMetadata,
    onDelete,
}: {
    selectedUserId: string;
    detail: DataMaintenanceStore.OrphanedUserDetail | null;
    loading: boolean;
    error: string;
    onRevealMetadata: () => void;
    onHideMetadata: () => void;
    onDelete: (userId: string) => void;
}) => {
    if (!selectedUserId) {
        return (
            <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex min-h-[20rem] flex-col items-center justify-center px-6 py-12 text-center text-sm text-gray-500">
                    <FontAwesomeIcon icon={["fad", "info-circle"]} className="mb-3 text-3xl text-gray-300" />
                    No account selected
                </div>
            </aside>
        );
    }

    if (loading) {
        return (
            <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex min-h-[20rem] flex-col items-center justify-center px-6 py-12 text-center text-sm text-gray-500">
                    <FontAwesomeIcon icon={["fad", "spinner-third"]} spin className="mb-3 text-2xl text-blue-500" />
                    Loading diagnostic detail
                </div>
            </aside>
        );
    }

    if (error) {
        return (
            <aside className="overflow-hidden rounded-lg border border-red-200 bg-white shadow-sm">
                <div className="px-4 py-4 text-sm text-red-800">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                    {error}
                </div>
            </aside>
        );
    }

    if (!detail) {
        return (
            <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="px-4 py-4 text-sm text-gray-500">No detail loaded</div>
            </aside>
        );
    }

    const metadataAction =
        detail.metadata.length > 0 ? (
            <Button size="sm" variant="outline-secondary" onClick={detail.metadataValuesIncluded ? onHideMetadata : onRevealMetadata}>
                <FontAwesomeIcon icon={["fad", detail.metadataValuesIncluded ? "eye-slash" : "eye"]} className="mr-2 h-3 w-3" />
                {detail.metadataValuesIncluded ? "Hide values" : "Reveal values"}
            </Button>
        ) : null;

    return (
        <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="break-words text-lg font-semibold text-gray-900">{displayName(detail)}</h2>
                        <div className="mt-1 break-all text-sm text-gray-600">{detail.email || detail.userName || detail.id}</div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                        <StatusBadge user={detail} />
                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(detail.id)} disabled={!detail.canDelete}>
                            <FontAwesomeIcon icon={["fad", "trash-alt"]} className="mr-2 h-3 w-3" />
                            Delete
                        </Button>
                    </div>
                </div>

                {!detail.isOrphanedUser && (
                    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                        Account now has enabled memberships. Destructive actions are unavailable.
                    </div>
                )}
            </div>

            <DetailSection title="Identity">
                <dl className="grid gap-3 sm:grid-cols-2">
                    <DetailField label="User ID" value={detail.id} mono />
                    <DetailField label="Username" value={detail.userName} />
                    <DetailField label="Email" value={detail.email} />
                    <DetailField label="Phone" value={detail.phoneNumber} />
                    <DetailField label="First name" value={detail.firstName} />
                    <DetailField label="Last name" value={detail.lastName} />
                    <DetailField label="Full name" value={detail.fullName} />
                    <DetailField label="Profile state" value={detail.isDisabled ? "Disabled" : "Enabled"} />
                </dl>
            </DetailSection>

            <DetailSection title="Protection">
                <div className="mb-3 flex flex-wrap gap-1.5">
                    <CountPill label="Enabled orgs" value={detail.enabledMembershipCount} />
                    <CountPill label="Disabled orgs" value={detail.disabledMembershipCount} />
                    <CountPill label="Providers" value={detail.providerLoginCount} />
                    <CountPill label="Claims" value={detail.claimCount} />
                    <CountPill label="Metadata" value={detail.metadataCount} />
                    <CountPill label="Groups" value={detail.groupCount} />
                    <CountPill label="Delegated" value={detail.delegatedAccessCount} />
                    <CountPill label="Credentials" value={detail.credentialCount} />
                    <CountPill label="Related" value={detail.relatedLocalRowCount} />
                </div>
                <dl className="grid gap-3 sm:grid-cols-3">
                    <DetailField label="Created" value={formatDate(detail.dateCreated)} />
                    <DetailField label="Updated" value={formatDate(detail.dateUpdated)} />
                    <DetailField label="Last login" value={formatDate(detail.lastLoginDate)} />
                </dl>
            </DetailSection>

            <DetailSection title="Provider Logins" count={detail.providerLogins.length}>
                {detail.providerLogins.length === 0 ? (
                    <EmptyDetail message="No provider logins" />
                ) : (
                    <div className="space-y-2">
                        {detail.providerLogins.map((login) => (
                            <div key={`${login.loginProvider}-${login.providerKey}`} className="rounded-md border border-gray-200 bg-white p-3">
                                <dl className="grid gap-3">
                                    <DetailField label="Provider" value={login.loginProvider} />
                                    <DetailField label="Display name" value={login.providerDisplayName} />
                                    <DetailField label="Provider key" value={login.providerKey} mono />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            <DetailSection title="Claims" count={detail.claims.length}>
                {detail.claims.length === 0 ? (
                    <EmptyDetail message="No claims" />
                ) : (
                    <div className="space-y-2">
                        {detail.claims.map((claim) => (
                            <div key={`${claim.id}-${claim.claimType}`} className="rounded-md border border-gray-200 bg-white p-3">
                                <dl className="grid gap-3">
                                    <DetailField label="Type" value={claim.claimType} mono />
                                    <DetailField label="Value" value={claim.claimValue} mono />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            {detail.enabledMemberships.length > 0 && (
                <DetailSection title="Enabled Memberships" count={detail.enabledMemberships.length}>
                    <MembershipList memberships={detail.enabledMemberships} />
                </DetailSection>
            )}

            <DetailSection title="Disabled Memberships" count={detail.disabledMemberships.length}>
                <MembershipList memberships={detail.disabledMemberships} />
            </DetailSection>

            <DetailSection title="Metadata" count={detail.metadata.length} action={metadataAction}>
                {detail.metadata.length === 0 ? (
                    <EmptyDetail message="No metadata" />
                ) : (
                    <div className="space-y-2">
                        {detail.metadata.map((metadata) => (
                            <div key={metadata.id} className="rounded-md border border-gray-200 bg-white p-3">
                                <dl className="grid gap-3">
                                    <DetailField label="Organization ID" value={metadata.organizationId} mono />
                                    <DetailField label="Key" value={metadata.key} mono />
                                    <DetailField label="Override enabled" value={metadata.enableOverride ? "Yes" : "No"} />
                                    <DetailField label="Value" value={detail.metadataValuesIncluded ? metadata.value : metadata.hasValue ? "Hidden" : ""} mono={detail.metadataValuesIncluded} />
                                    <DetailField
                                        label="Override value"
                                        value={detail.metadataValuesIncluded ? metadata.overrideValue : metadata.hasOverrideValue ? "Hidden" : ""}
                                        mono={detail.metadataValuesIncluded}
                                    />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            <DetailSection title="Groups" count={detail.groups.length}>
                {detail.groups.length === 0 ? (
                    <EmptyDetail message="No groups" />
                ) : (
                    <div className="space-y-2">
                        {detail.groups.map((group) => (
                            <div key={`${group.groupId}-${group.membershipType}`} className="rounded-md border border-gray-200 bg-white p-3">
                                <div className="text-sm font-medium text-gray-900">{group.groupName || group.groupId}</div>
                                <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <DetailField label="Group ID" value={group.groupId} mono />
                                    <DetailField label="Organization ID" value={group.organizationId} mono />
                                    <DetailField label="Type" value={group.type} />
                                    <DetailField label="Membership" value={group.membershipType} />
                                    <DetailField label="Source" value={group.source} />
                                    <DetailField label="Source ID" value={group.sourceId} mono />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            <DetailSection title="Delegated Access" count={detail.delegatedAccess.length}>
                {detail.delegatedAccess.length === 0 ? (
                    <EmptyDetail message="No delegated access" />
                ) : (
                    <div className="space-y-2">
                        {detail.delegatedAccess.map((access) => (
                            <div key={`${access.delegatorId}-${access.delegateeId}`} className="rounded-md border border-gray-200 bg-white p-3">
                                <div className="text-sm font-medium text-gray-900">{access.direction}</div>
                                <dl className="mt-3 grid gap-3">
                                    <DetailField label="Delegator ID" value={access.delegatorId} mono />
                                    <DetailField label="Delegatee ID" value={access.delegateeId} mono />
                                    <DetailField label="Rules" value={access.delegationRules} mono />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            <DetailSection title="Credentials" count={detail.credentials.length}>
                {detail.credentials.length === 0 ? (
                    <EmptyDetail message="No credentials" />
                ) : (
                    <div className="space-y-2">
                        {detail.credentials.map((credential) => (
                            <div key={credential.id} className="rounded-md border border-gray-200 bg-white p-3">
                                <dl className="grid gap-3 sm:grid-cols-2">
                                    <DetailField label="ID" value={credential.id} mono />
                                    <DetailField label="Device ID" value={credential.deviceId} mono />
                                    <DetailField label="Type" value={credential.credentialType} />
                                    <DetailField label="Registered" value={formatDate(credential.registrationDate)} />
                                </dl>
                            </div>
                        ))}
                    </div>
                )}
            </DetailSection>

            <DetailSection title="Related Local Rows" count={detail.relatedLocalRowCount}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {detail.relatedLocalRows.map((row) => (
                        <div key={row.name} className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                            <span className="break-words text-gray-700">{row.name}</span>
                            <span className="ml-3 font-semibold text-gray-900">{row.count}</span>
                        </div>
                    ))}
                </div>
            </DetailSection>
        </aside>
    );
};

export const DataMaintenanceIndex = () => {
    const [users, setUsers] = React.useState([] as DataMaintenanceStore.OrphanedUserSummary[]);
    const [total, setTotal] = React.useState(0);
    const [skip, setSkip] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [query, setQuery] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [selectedUserId, setSelectedUserId] = React.useState("");
    const [detail, setDetail] = React.useState<DataMaintenanceStore.OrphanedUserDetail | null>(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState("");
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
    const [deleteDetail, setDeleteDetail] = React.useState<DataMaintenanceStore.OrphanedUserDetail | null>(null);
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleteSaving, setDeleteSaving] = React.useState(false);
    const [deleteResult, setDeleteResult] = React.useState<DataMaintenanceStore.DeleteOrphanedUserResult | null>(null);

    const loadUsers = (nextSkip = skip, nextLimit = limit, nextQuery = query) => {
        setLoading(true);
        setError("");
        DataMaintenanceStore.ListOrphanedUsers(nextQuery, nextSkip, nextLimit)
            .then((response) => {
                setUsers(response.users);
                setTotal(response.total);
                setSkip(response.skip);
                setLimit(response.limit);
                setLoading(false);

                if (selectedUserId && !response.users.some((user) => user.id === selectedUserId)) {
                    setSelectedUserId("");
                    setDetail(null);
                    setDetailError("");
                }
            })
            .catch((request) => {
                setUsers([]);
                setTotal(0);
                setSelectedUserId("");
                setDetail(null);
                setLoading(false);
                if (request.response?.status === 401 || request.response?.status === 403) {
                    setError("Data Maintenance is only available to SuperAdmins.");
                } else {
                    setError("Unable to load orphaned accounts.");
                }
            });
    };

    const loadDetail = (userId: string, includeMetadataValues = false) => {
        setSelectedUserId(userId);
        setDetailLoading(true);
        setDetailError("");
        DataMaintenanceStore.GetOrphanedUser(userId, includeMetadataValues)
            .then((response) => {
                setDetail(response);
                setDetailLoading(false);
            })
            .catch((request) => {
                setDetail(null);
                setDetailLoading(false);
                if (request.response?.status === 404) {
                    setDetailError("The selected account could not be found.");
                } else if (request.response?.status === 401 || request.response?.status === 403) {
                    setDetailError("Diagnostic detail is only available to SuperAdmins.");
                } else {
                    setDetailError("Unable to load diagnostic detail.");
                }
            });
    };

    React.useEffect(() => {
        loadUsers(0, limit, query);
    }, []);

    const search = (event: React.FormEvent) => {
        event.preventDefault();
        loadUsers(0, limit, query);
    };

    const changeLimit = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextLimit = parseInt(event.target.value, 10);
        loadUsers(0, nextLimit, query);
    };

    const nextPage = () => {
        if (skip + limit < total) {
            loadUsers(skip + limit, limit, query);
        }
    };

    const previousPage = () => {
        loadUsers(Math.max(0, skip - limit), limit, query);
    };

    const revealMetadataValues = () => {
        if (detail) {
            loadDetail(detail.id, true);
        }
    };

    const hideMetadataValues = () => {
        if (detail) {
            loadDetail(detail.id, false);
        }
    };

    const openDeleteModal = (userId: string) => {
        setDeleteModalOpen(true);
        setDeleteDetail(null);
        setDeleteError("");
        setDeleteSaving(false);
        setDeleteResult(null);
        setDeleteLoading(true);

        DataMaintenanceStore.GetOrphanedUser(userId, false)
            .then((response) => {
                setDeleteDetail(response);
                setDeleteLoading(false);
            })
            .catch((request) => {
                setDeleteDetail(null);
                setDeleteLoading(false);
                if (request.response?.status === 404) {
                    setDeleteError("The selected account could not be found.");
                } else if (request.response?.status === 401 || request.response?.status === 403) {
                    setDeleteError("Deletion is only available to SuperAdmins.");
                } else {
                    setDeleteError("Unable to load current account diagnostics.");
                }
            });
    };

    const closeDeleteModal = () => {
        if (deleteSaving) {
            return;
        }

        setDeleteModalOpen(false);
        setDeleteDetail(null);
        setDeleteError("");
        setDeleteResult(null);
    };

    const confirmDelete = () => {
        if (!deleteDetail || !deleteDetail.canDelete || deleteSaving) {
            return;
        }

        setDeleteSaving(true);
        setDeleteError("");
        setDeleteResult(null);

        DataMaintenanceStore.DeleteOrphanedUser(deleteDetail.id)
            .then((response) => {
                setDeleteSaving(false);
                setDeleteResult(response);
                loadUsers(skip, limit, query);

                if (response.deleted) {
                    if (selectedUserId === deleteDetail.id) {
                        setSelectedUserId("");
                        setDetail(null);
                        setDetailError("");
                    }
                } else if (response.detail) {
                    setDeleteDetail(response.detail);
                    if (selectedUserId === response.userId) {
                        setDetail(response.detail);
                    }
                }
            })
            .catch((request) => {
                setDeleteSaving(false);

                const response = request.response?.data as DataMaintenanceStore.DeleteOrphanedUserResult;
                if (response) {
                    setDeleteResult(response);
                    if (response.detail) {
                        setDeleteDetail(response.detail);
                        if (selectedUserId === response.userId) {
                            setDetail(response.detail);
                        }
                    }
                    if (response.notFound) {
                        setDeleteError(response.message || "The selected account could not be found.");
                    }
                    loadUsers(skip, limit, query);
                    return;
                }

                if (request.response?.status === 401 || request.response?.status === 403) {
                    setDeleteError("Deletion is only available to SuperAdmins.");
                } else {
                    setDeleteError("Unable to delete the account.");
                }
            });
    };

    const eligibleCount = users.filter((u) => u.canDelete).length;
    const protectedCount = users.length - eligibleCount;

    return (
        <div className="mx-auto max-w-[92rem] px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                        <FontAwesomeIcon icon={["fad", "database"]} className="text-xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Data Maintenance</h1>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                            <span className="font-medium text-gray-800">Orphaned Accounts</span>
                            <span className="text-gray-300">/</span>
                            <span>{total} candidates</span>
                            <span className="text-gray-300">/</span>
                            <span>{eligibleCount} eligible on page</span>
                            <span className="text-gray-300">/</span>
                            <span>{protectedCount} protected on page</span>
                        </div>
                    </div>
                </div>

                <form onSubmit={search} className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                    <div className="relative min-w-0 sm:w-96">
                        <FontAwesomeIcon icon={["fad", "search"]} className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Search accounts, providers, claims, organizations"
                        />
                    </div>
                    <Button type="submit" disabled={loading}>
                        <FontAwesomeIcon icon={["fad", "search"]} className="mr-2 h-4 w-4" />
                        Search
                    </Button>
                </form>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                    {error}
                </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_30rem]">
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Account</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">State</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Evidence</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Protection</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Dates</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                                            <FontAwesomeIcon icon={["fad", "spinner-third"]} spin className="mr-2" />
                                            Loading orphaned accounts
                                        </td>
                                    </tr>
                                )}

                                {!loading && users.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                                            <FontAwesomeIcon icon={["fad", "inbox"]} className="mb-2 block w-full text-3xl text-gray-300" />
                                            No orphaned accounts found
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    users.map((user) => (
                                        <tr key={user.id} className={selectedUserId === user.id ? "bg-blue-50" : "hover:bg-gray-50"}>
                                            <td className="max-w-sm px-4 py-4 align-top">
                                                <div className="text-sm font-semibold text-gray-900">{displayName(user)}</div>
                                                <div className="mt-1 text-sm text-gray-600">{user.email || "-"}</div>
                                                <div className="mt-1 truncate font-mono text-xs text-gray-500" title={user.id}>
                                                    {user.id}
                                                </div>
                                                {user.phoneNumber && <div className="mt-1 text-xs text-gray-500">{user.phoneNumber}</div>}
                                            </td>
                                            <td className="px-4 py-4 align-top">
                                                <div className="flex flex-wrap gap-1.5">
                                                    <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                                                        {user.isDisabled ? "Disabled" : "Enabled profile"}
                                                    </span>
                                                    {user.isAdmin && (
                                                        <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-200">
                                                            Admin
                                                        </span>
                                                    )}
                                                    {user.isSuperAdmin && (
                                                        <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-200">
                                                            SuperAdmin
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="min-w-[20rem] px-4 py-4 align-top">
                                                <div className="flex flex-wrap gap-1.5">
                                                    <CountPill label="Providers" value={user.providerLoginCount} />
                                                    <CountPill label="Claims" value={user.claimCount} />
                                                    <CountPill label="Disabled orgs" value={user.disabledMembershipCount} />
                                                    <CountPill label="Metadata" value={user.metadataCount} />
                                                    <CountPill label="Groups" value={user.groupCount} />
                                                    <CountPill label="Delegated" value={user.delegatedAccessCount} />
                                                    <CountPill label="Credentials" value={user.credentialCount} />
                                                    <CountPill label="Related" value={user.relatedLocalRowCount} />
                                                </div>
                                            </td>
                                            <td className="min-w-[14rem] px-4 py-4 align-top">
                                                <StatusBadge user={user} />
                                            </td>
                                            <td className="min-w-[14rem] px-4 py-4 align-top text-xs text-gray-600">
                                                <div>
                                                    <span className="font-medium text-gray-700">Created:</span> {formatDate(user.dateCreated)}
                                                </div>
                                                <div className="mt-1">
                                                    <span className="font-medium text-gray-700">Updated:</span> {formatDate(user.dateUpdated)}
                                                </div>
                                                <div className="mt-1">
                                                    <span className="font-medium text-gray-700">Last login:</span> {formatDate(user.lastLoginDate)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 align-top">
                                                <div className="flex flex-col items-start gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant={selectedUserId === user.id ? "primary" : "outline-secondary"}
                                                        onClick={() => loadDetail(user.id)}
                                                        disabled={detailLoading && selectedUserId === user.id}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={["fad", detailLoading && selectedUserId === user.id ? "spinner-third" : "info-circle"]}
                                                            spin={detailLoading && selectedUserId === user.id}
                                                            className="mr-2 h-3 w-3"
                                                        />
                                                        Details
                                                    </Button>
                                                    {user.canDelete && (
                                                        <Button size="sm" variant="outline-danger" onClick={() => openDeleteModal(user.id)}>
                                                            <FontAwesomeIcon icon={["fad", "trash-alt"]} className="mr-2 h-3 w-3" />
                                                            Delete
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span>
                                {total === 0 ? 0 : skip + 1}-{Math.min(skip + limit, total)} of {total}
                            </span>
                            <select value={limit} onChange={changeLimit} className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500">
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline-secondary" onClick={previousPage} disabled={loading || skip === 0}>
                                <FontAwesomeIcon icon={["fad", "chevron-left"]} className="mr-2 h-3 w-3" />
                                Previous
                            </Button>
                            <Button variant="outline-secondary" onClick={nextPage} disabled={loading || skip + limit >= total}>
                                Next
                                <FontAwesomeIcon icon={["fad", "chevron-right"]} className="ml-2 h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                </div>

                <DetailPanel
                    selectedUserId={selectedUserId}
                    detail={detail}
                    loading={detailLoading}
                    error={detailError}
                    onRevealMetadata={revealMetadataValues}
                    onHideMetadata={hideMetadataValues}
                    onDelete={openDeleteModal}
                />
            </div>

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                detail={deleteDetail}
                loading={deleteLoading}
                error={deleteError}
                deleting={deleteSaving}
                result={deleteResult}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
            />
        </div>
    );
};
